import React, { useState, useEffect } from 'react'
import { Box, Button, IconButton, Typography } from '@mui/material'
import { Close, AutoAwesome } from '@mui/icons-material'
import { useGetWeeklyContextStatusQuery, useGetListeningTopicsQuery } from 'graphql/generated/graphql'
import WeeklyContextModal from 'components/WeeklyContextModal/WeeklyContextModal'

const DISMISS_KEY = 'weeklyContextBannerDismissed'

const getWeekStart = (): string => {
  const now = new Date()
  const day = now.getUTCDay()
  const diff = now.getUTCDate() - day + (day === 0 ? -6 : 1)
  const monday = new Date(now.setUTCDate(diff))
  monday.setUTCHours(0, 0, 0, 0)
  return monday.toISOString().split('T')[0]
}

export default function WeeklyContextBanner(): JSX.Element | null {
  const [dismissed, setDismissed] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)

  const { data, loading, refetch } = useGetWeeklyContextStatusQuery()
  const { data: topicsData } = useGetListeningTopicsQuery()

  // If user has a connected podcast, context is auto-managed — don't nag
  const hasConnectedPodcast = (topicsData?.getListeningTopics ?? []).some(
    (t: any) => t.name?.startsWith('Mentions:') // Presence of a Mentions signal indicates podcast onboarding
  )

  useEffect(() => {
    // Check if user dismissed the banner this week
    const dismissedWeek = localStorage.getItem(DISMISS_KEY)
    const currentWeek = getWeekStart()
    if (dismissedWeek === currentWeek) {
      setDismissed(true)
    } else {
      setDismissed(false)
    }
  }, [])

  const handleDismiss = () => {
    const currentWeek = getWeekStart()
    localStorage.setItem(DISMISS_KEY, currentWeek)
    setDismissed(true)
  }

  const handleModalSuccess = () => {
    void refetch()
  }

  // Don't show if loading, dismissed, has context for current week, or has connected podcast (auto-managed)
  const shouldHide = loading || dismissed || (data?.getWeeklyContextStatus?.hasCurrentWeek === true) || hasConnectedPodcast
  if (shouldHide) {
    return null
  }

  return (
    <>
      <Box
        sx={{
          background: 'linear-gradient(135deg, rgba(168,85,247,0.15) 0%, rgba(236,72,153,0.15) 100%)',
          border: '1px solid rgba(168,85,247,0.3)',
          borderRadius: 2,
          p: 2,
          mx: { xs: 1.5, sm: 3 },
          mt: 2,
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'flex-start', sm: 'center' },
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <AutoAwesome sx={{ color: '#a855f7', fontSize: 28, flexShrink: 0 }} />
          <Box>
            <Typography sx={{ color: '#fff', fontWeight: 600 }}>
              Keep your comments fresh!
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 14 }}>
              Share what you&apos;ve been up to this week to make AI comments more personal and organic.
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: { xs: '100%', sm: 'auto' }, justifyContent: { xs: 'flex-end', sm: 'flex-start' } }}>
          <Button
            variant="contained"
            onClick={() => setModalOpen(true)}
            size="small"
            sx={{
              background: 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #9333ea 0%, #db2777 100%)',
              },
              whiteSpace: 'nowrap',
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              px: { xs: 1.5, sm: 2 },
            }}
          >
            Update Weekly Context
          </Button>
          <IconButton
            onClick={handleDismiss}
            size="small"
            sx={{
              color: 'rgba(255,255,255,0.5)',
              '&:hover': { color: '#fff' },
            }}
          >
            <Close />
          </IconButton>
        </Box>
      </Box>

      <WeeklyContextModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSuccess={handleModalSuccess}
      />
    </>
  )
}
